// src/services/socketService.ts

/**
 * Socket Service - Production-grade Socket.IO client.
 * Powers nuvrahq.com's real-time collaboration, presence,
 * build pipeline, and deployment event streaming.
 *
 * Features:
 *   • Fully typed event system (no more `any`)
 *   • Presence events (who's online, cursor positions, active file)
 *   • Build pipeline events (log, status, complete, error, progress)
 *   • Deployment events (stage, progress, publicUrl wired)
 *   • Schema sync events (broadcast, apply, conflict)
 *   • Session events (join, leave, lock, operation)
 *   • Room management (join/leave project rooms)
 *   • Reconnection with exponential backoff
 *   • Heartbeat / keep-alive
 *   • Event queue (buffers events while offline, flushes on reconnect)
 *   • Per-event listener registry with typed unsubscribe
 *   • Connection health monitoring
 *   • Auth token injection
 *
 * nuvrahq.com
 */

import io, { Socket } from "socket.io-client";
import { logger }     from "@/system/logger";
import { getEnvVar }  from "@/config/env";

/* =========================================================
   CONSTANTS
   ========================================================= */

// __API_URL__ is baked at build time by vite.config.ts from VITE_API_URL.
// Falls back to VITE_API_URL via getEnvVar (process.env / window.__ENV__).
// For local dev: set VITE_API_URL=http://localhost:3000 in .env.local.
declare const __API_URL__: string | undefined;
const BACKEND_URL =
  getEnvVar("VITE_API_URL") ||
  (typeof __API_URL__ !== "undefined" ? __API_URL__ : "") ||
  "";
const HEARTBEAT_INTERVAL_MS  = 25_000;
const MAX_EVENT_QUEUE_SIZE   = 100;
const RECONNECTION_ATTEMPTS  = 10;
const RECONNECTION_DELAY_MS  = 1_000;
const RECONNECTION_DELAY_MAX = 10_000;

/* =========================================================
   TYPES - BUILD EVENTS
   ========================================================= */

export interface BuildLogEvent {
  projectId:  string;
  message:    string;
  level:      "info" | "warn" | "error" | "success" | "debug";
  timestamp:  number;
  stage?:     string;
}

export interface BuildStatusEvent {
  projectId:   string;
  status:      "queued" | "building" | "complete" | "failed" | "cancelled";
  stage?:      string;
  percentage?: number;
  timestamp:   number;
}

export interface BuildCompleteEvent {
  projectId:  string;
  success:    boolean;
  publicUrl?: string;   // nuvrahq.com/p/:projectId - wired from autoDeploymentManager
  slugUrl?:   string;   // nuvrahq.com/app/:slug
  liveUrl?:   string;   // nuvrahq.com/live/:projectId
  duration:   number;
  timestamp:  number;
}

export interface BuildErrorEvent {
  projectId: string;
  message:   string;
  title?:    string;
  hint?:     string;
  category?: string;
  retryable?: boolean;
  stage?:    string;
  timestamp: number;
}

export interface BuildStartedEvent {
  projectId: string;
  buildId?:  string;
}

export interface BuildFailedEvent {
  projectId: string;
  buildId?:  string;
  reason:    string;
}

export interface PipelineStageEvent {
  event:      "start" | "complete" | "failed" | "retry";
  buildId:    string;
  projectId:  string;
  stage:      string;
  durationMs?: number;
  error?:      string;
  attempt?:    number;
  maxAttempts?: number;
  delayMs?:    number;
}

/* =========================================================
   TYPES - DEPLOYMENT EVENTS
   ========================================================= */

export interface DeploymentProgressEvent {
  deploymentId: string;
  projectId:    string;
  stage:        string;
  percentage:   number;
  message:      string;
  publicUrl?:   string;
  timestamp:    number;
}

export interface DeploymentCompleteEvent {
  deploymentId: string;
  projectId:    string;
  success:      boolean;
  publicUrl?:   string;
  slugUrl?:     string;
  liveUrl?:     string;
  duration:     number;
  timestamp:    number;
}

export interface DeploymentFailedEvent {
  deploymentId: string;
  projectId:    string;
  error:        string;
  stage?:       string;
  timestamp:    number;
}

/* =========================================================
   TYPES - PRESENCE EVENTS
   ========================================================= */

export interface PresenceJoinEvent {
  projectId:   string;
  userId:      string;
  displayName: string;
  avatarUrl?:  string;
  color:       string;
  role:        string;
  sessionId:   string;
  timestamp:   number;
}

export interface PresenceLeaveEvent {
  projectId:  string;
  userId:     string;
  sessionId:  string;
  timestamp:  number;
}

export interface PresenceCursorEvent {
  projectId:  string;
  userId:     string;
  color:      string;
  fileId:     string;
  line:       number;
  column:     number;
  selection?: {
    startLine:   number;
    startColumn: number;
    endLine:     number;
    endColumn:   number;
  };
  timestamp:  number;
}

export interface PresenceFileEvent {
  projectId:   string;
  userId:      string;
  displayName: string;
  color:       string;
  fileId:      string;
  timestamp:   number;
}

export interface PresenceStatusEvent {
  projectId:   string;
  userId:      string;
  displayName: string;
  status:      "active" | "idle" | "away" | "offline";
  timestamp:   number;
}

export interface PresenceListEvent {
  projectId: string;
  users:     {
    userId:      string;
    displayName: string;
    avatarUrl?:  string;
    color:       string;
    role:        string;
    status:      string;
    activeFile?: string;
  }[];
}

/* =========================================================
   TYPES - AI AGENT PRESENCE EVENTS
   ========================================================= */

export interface AgentJoinEvent {
  projectId:        string;
  agentId:          string;
  agentType:        "claude" | "codex" | "custom";
  displayName:      string;
  color:            string;
  taskDescription?: string;
  timestamp:        number;
}

export interface AgentStateEvent {
  projectId:    string;
  agentId:      string;
  state:        "idle" | "thinking" | "editing" | "reviewing" | "waiting_for_human";
  activeFileId?: string;
  taskDescription?: string;
  timestamp:    number;
}

export interface AgentLeaveEvent {
  projectId: string;
  agentId:   string;
  timestamp: number;
}

/* =========================================================
   TYPES - SESSION EVENTS
   ========================================================= */

export interface SessionCreatedEvent {
  projectId:  string;
  sessionId:  string;
  ownerId:    string;
  timestamp:  number;
}

export interface SessionClosedEvent {
  projectId:  string;
  sessionId:  string;
  reason:     string;
  timestamp:  number;
}

export interface FileLockEvent {
  projectId:   string;
  sessionId:   string;
  fileId:      string;
  userId:      string;
  displayName: string;
  color:       string;
  strength:    "soft" | "hard";
  lockId:      string;
  timestamp:   number;
}

export interface FileLockReleasedEvent {
  projectId: string;
  sessionId: string;
  fileId:    string;
  lockId:    string;
  userId:    string;
  reason?:   string;
  timestamp: number;
}

export interface OperationEvent {
  projectId:   string;
  sessionId:   string;
  operationId: string;
  userId:      string;
  type:        string;
  fileId?:     string;
  payload:     Record<string, unknown>;
  timestamp:   number;
}

/* =========================================================
   TYPES - SCHEMA SYNC EVENTS
   ========================================================= */

export interface SchemaSyncEvent {
  projectId:   string;
  changeId:    string;
  userId:      string;
  path:        string;
  operation:   string;
  data:        unknown;
  vectorClock: Record<string, number>;
  timestamp:   number;
}

export interface SchemaConflictEvent {
  projectId:   string;
  conflictId:  string;
  path:        string;
  localUserId: string;
  timestamp:   number;
}

/* =========================================================
   TYPES - SYNC STATUS EVENTS
   ========================================================= */

export interface SyncStatusEvent {
  projectId:     string;
  status:        string;
  pendingCount:  number;
  conflictCount: number;
  lastSyncedAt:  number;
  timestamp:     number;
}

/* =========================================================
   TYPES - SECURITY TELEMETRY EVENTS
   ========================================================= */

export type SecurityEventKind =
  | "threat"
  | "quarantine"
  | "rate_limit"
  | "validation_fail"
  | "ai_threat";

export interface SecurityTelemetryEvent {
  projectId:   string;
  kind:        SecurityEventKind;
  actorId:     string;
  threatLevel: "none" | "low" | "medium" | "high" | "critical";
  threats:     string[];
  eventType:   string;
  blocked:     boolean;
  timestamp:   number;
}

/* =========================================================
   TYPES - AI STAGE EVENTS
   ========================================================= */

export interface AgentStageEvent {
  projectId:    string;
  agentId:      string;
  stage:        string;
  fromStage:    string;
  agentState:   string;  // coarse state (backward compat with agent:state)
  label:        string;
  description:  string;
  pulse:        boolean;
  color:        string;
  durationMs?:  number;
  sequenceNum:  number;
  metadata?:    {
    taskDescription?: string;
    activeFileId?:    string;
    attempt?:         number;
    maxAttempts?:     number;
    errorMessage?:    string;
    progressPct?:     number;
    dependencyName?:  string;
    rollbackReason?:  string;
  };
  timestamp:    number;
}

/* =========================================================
   TYPES - FULL TYPED EVENT MAP
   ─────────────────────────────────────────────────────────
   Maps every socket event name to its payload type.
   Used to enforce types on on() and emit() calls.
   ========================================================= */

export interface SocketEventMap {
  // ── Build ──────────────────────────────────────────────
  "build:log":               BuildLogEvent;
  "build:status":            BuildStatusEvent;
  "build:complete":          BuildCompleteEvent;
  "build:error":             BuildErrorEvent;
  "build:started":           BuildStartedEvent;
  "build:failed":            BuildFailedEvent;
  "pipeline:stage":          PipelineStageEvent;

  // ── Deployment ─────────────────────────────────────────
  "deployment:progress":     DeploymentProgressEvent;
  "deployment:complete":     DeploymentCompleteEvent;
  "deployment:failed":       DeploymentFailedEvent;

  // ── Presence ───────────────────────────────────────────
  "presence:join":           PresenceJoinEvent;
  "presence:leave":          PresenceLeaveEvent;
  "presence:cursor":         PresenceCursorEvent;
  "presence:file":           PresenceFileEvent;
  "presence:status":         PresenceStatusEvent;
  "presence:list":           PresenceListEvent;

  // ── AI Agents ──────────────────────────────────────────
  "agent:join":              AgentJoinEvent;
  "agent:state":             AgentStateEvent;
  "agent:stage":             AgentStageEvent;
  "agent:leave":             AgentLeaveEvent;

  // ── Security Telemetry ─────────────────────────────────
  "security:event":          SecurityTelemetryEvent;

  // ── Session ────────────────────────────────────────────
  "session:created":         SessionCreatedEvent;
  "session:closed":          SessionClosedEvent;
  "lock:acquired":           FileLockEvent;
  "lock:released":           FileLockReleasedEvent;
  "lock:contested":          FileLockEvent;
  "operation:recorded":      OperationEvent;

  // ── Schema Sync ────────────────────────────────────────
  "schema:update":           SchemaSyncEvent;
  "schema:conflict":         SchemaConflictEvent;

  // ── Sync Status ────────────────────────────────────────
  "sync:status":             SyncStatusEvent;

  // ── System ─────────────────────────────────────────────
  "connect":                 undefined;
  "disconnect":              string;
  "connect_error":           Error;
  "reconnect":               number;
  "reconnect_attempt":       number;
  "reconnect_failed":        undefined;
  "heartbeat:ack":           { timestamp: number };
}

/* =========================================================
   TYPES - OUTBOUND EMIT MAP
   ─────────────────────────────────────────────────────────
   Maps every client-to-server event to its payload type.
   ========================================================= */

export interface SocketEmitMap {
  "room:join":               { projectId: string; userId: string; token?: string };
  "room:leave":              { projectId: string; userId: string };
  "presence:cursor":         Omit<PresenceCursorEvent, "timestamp">;
  "presence:file":           Omit<PresenceFileEvent, "timestamp">;
  "presence:status":         Omit<PresenceStatusEvent, "timestamp">;
  "schema:broadcast":        Omit<SchemaSyncEvent, "timestamp">;
  "heartbeat:ping":          { timestamp: number };
  "build:subscribe":         { projectId: string };
  "build:unsubscribe":       { projectId: string };
  "deployment:subscribe":    { projectId: string; deploymentId?: string };
  "deployment:unsubscribe":  { projectId: string };
}

/* =========================================================
   TYPES - CONNECTION STATE
   ========================================================= */

export type SocketConnectionStatus =
  | "connecting"
  | "connected"
  | "disconnected"
  | "reconnecting"
  | "failed";

export interface SocketConnectionState {
  status:            SocketConnectionStatus;
  socketId:          string | null;
  connectedAt:       number | null;
  reconnectAttempts: number;
  latencyMs:         number | null;
  rooms:             Set<string>;
}

/* =========================================================
   TYPES - QUEUED EVENT
   ========================================================= */

interface QueuedEvent {
  event:     keyof SocketEmitMap;
  data:      unknown;
  queuedAt:  number;
}

/* =========================================================
   SOCKET SERVICE CLASS
   ========================================================= */

class SocketService {

  /* =========================================================
     INTERNAL STATE
     ========================================================= */

  private socket:            Socket | null = null;
  private authToken:         string | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval>  | null = null;
  private pingTimestamp:     number | null = null;
  private eventQueue:        QueuedEvent[] = [];

  private connectionState: SocketConnectionState = {
    status:            "disconnected",
    socketId:          null,
    connectedAt:       null,
    reconnectAttempts: 0,
    latencyMs:         null,
    rooms:             new Set(),
  };

  /** Typed listener registry - event → set of callbacks */
  private listeners = new Map<string, Set<(data: unknown) => void>>();

  /** Connection state change listeners */
  private connectionListeners = new Set<(state: SocketConnectionState) => void>();

  /* =========================================================
     🔥 INITIALIZE
     ========================================================= */

  /**
   * Initialises the Socket.IO connection.
   * Idempotent - returns existing socket if already connected.
   */
  initialize(options: {
    authToken?:   string;
    onConnect?:   () => void;
    onDisconnect?: () => void;
    onError?:     (err: Error) => void;
  } = {}): Socket {
    if (this.socket?.connected) {
      logger.info("[SocketService] Already connected, returning existing socket", {
        socketId: this.socket.id,
      });
      return this.socket;
    }

    if (options.authToken) {
      this.authToken = options.authToken;
    }

    logger.info("[SocketService] Initialising Socket.IO", { url: BACKEND_URL });

    this.socket = io(BACKEND_URL, {
      reconnection:          true,
      reconnectionDelay:     RECONNECTION_DELAY_MS,
      reconnectionDelayMax:  RECONNECTION_DELAY_MAX,
      reconnectionAttempts:  RECONNECTION_ATTEMPTS,
      auth:                  this.authToken ? { token: this.authToken } : undefined,
      transports:            ["websocket", "polling"],
      timeout:               10_000,
    });

    this.registerCoreHandlers(options);
    this.updateConnectionState({ status: "connecting" });

    return this.socket;
  }

  /* =========================================================
     🔥 CORE SOCKET HANDLERS
     ========================================================= */

  private registerCoreHandlers(options: {
    onConnect?:    () => void;
    onDisconnect?: () => void;
    onError?:      (err: Error) => void;
  }): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      logger.info("[SocketService] Connected", { socketId: this.socket?.id });

      this.updateConnectionState({
        status:            "connected",
        socketId:          this.socket?.id ?? null,
        connectedAt:       Date.now(),
        reconnectAttempts: 0,
      });

      // Flush queued events
      this.flushEventQueue();

      // Start heartbeat
      this.startHeartbeat();

      options.onConnect?.();
    });

    this.socket.on("disconnect", (reason: string) => {
      logger.warn("[SocketService] Disconnected", { reason });

      this.stopHeartbeat();
      this.updateConnectionState({
        status:   "disconnected",
        socketId: null,
      });

      options.onDisconnect?.();
    });

    this.socket.on("connect_error", (err: Error) => {
      logger.error("[SocketService] Connection error", { message: err.message });
      this.updateConnectionState({ status: "reconnecting" });
      options.onError?.(err);
    });

    this.socket.on("reconnect", (attemptNumber: number) => {
      logger.info("[SocketService] Reconnected", { attemptNumber });
      this.updateConnectionState({
        status:            "connected",
        reconnectAttempts: attemptNumber,
      });

      // Re-join all rooms after reconnect
      this.rejoinRooms();
    });

    this.socket.on("reconnect_attempt", (attemptNumber: number) => {
      logger.info("[SocketService] Reconnect attempt", { attemptNumber });
      this.updateConnectionState({
        status:            "reconnecting",
        reconnectAttempts: attemptNumber,
      });
    });

    this.socket.on("reconnect_failed", () => {
      logger.error("[SocketService] Reconnection failed - giving up");
      this.updateConnectionState({ status: "failed" });
    });

    // Heartbeat ack - measures round-trip latency
    this.socket.on("heartbeat:ack", (data: { timestamp: number }) => {
      if (this.pingTimestamp) {
        const latencyMs = Date.now() - this.pingTimestamp;
        this.updateConnectionState({ latencyMs });
        this.pingTimestamp = null;
      }
    });

    // Wire all typed events to the listener registry
    this.wireTypedEvents();
  }

  /* =========================================================
     🔥 TYPED EVENT WIRING
     ========================================================= */

  /**
   * Forwards all known socket events into the typed listener registry.
   * Add new event names here when extending the protocol.
   */
  private wireTypedEvents(): void {
    if (!this.socket) return;

    const events: (keyof SocketEventMap)[] = [
      // Build
      "build:log", "build:status", "build:complete", "build:error",
      // Deployment
      "deployment:progress", "deployment:complete", "deployment:failed",
      // Presence
      "presence:join", "presence:leave", "presence:cursor",
      "presence:file", "presence:status", "presence:list",
      // Agents
      "agent:join", "agent:state", "agent:leave",
      // Session
      "session:created", "session:closed",
      "lock:acquired", "lock:released", "lock:contested",
      "operation:recorded",
      // Schema sync
      "schema:update", "schema:conflict",
      // Sync status
      "sync:status",
    ];

    for (const event of events) {
      this.socket.on(event, (data: unknown) => {
        const set = this.listeners.get(event);
        if (set) {
          set.forEach(fn => { try { fn(data); } catch (e) {
            logger.error("[SocketService] Listener error", { event, error: e });
          }});
        }
      });
    }
  }

  /* =========================================================
     🔥 TYPED SUBSCRIBE
     ========================================================= */

  /**
   * Subscribes to a typed socket event.
   * Returns an unsubscribe function.
   *
   * @example
   *   const unsub = socketService.on("presence:cursor", e => updateCursor(e))
   *   // later:
   *   unsub()
   */
  on<K extends keyof SocketEventMap>(
    event:    K,
    listener: (data: SocketEventMap[K]) => void
  ): () => void {
    const set = this.listeners.get(event) ?? new Set();
    // Cast needed because the Map stores Set<(data: unknown) => void>
    set.add(listener as (data: unknown) => void);
    this.listeners.set(event, set);

    return () => {
      const s = this.listeners.get(event);
      if (s) {
        s.delete(listener as (data: unknown) => void);
        if (s.size === 0) this.listeners.delete(event);
      }
    };
  }

  /**
   * One-time typed event listener.
   * Auto-unsubscribes after first fire.
   */
  once<K extends keyof SocketEventMap>(
    event:    K,
    listener: (data: SocketEventMap[K]) => void
  ): () => void {
    const unsub = this.on(event, (data) => {
      listener(data);
      unsub();
    });
    return unsub;
  }

  /* =========================================================
     🔥 TYPED EMIT
     ========================================================= */

  /**
   * Emits a typed event to the server.
   * If not connected, queues the event for when connection resumes.
   */
  emit<K extends keyof SocketEmitMap>(
    event: K,
    data:  SocketEmitMap[K]
  ): void {
    if (!this.socket?.connected) {
      logger.warn("[SocketService] Not connected - queuing event", { event });
      this.queueEvent(event, data);
      return;
    }

    logger.info("[SocketService] Emit", { event });
    this.socket.emit(event, data);
  }

  /* =========================================================
     🔥 ROOM MANAGEMENT
     ========================================================= */

  /**
   * Joins a project room to receive all events for that project.
   * Auto-rejoins on reconnect.
   */
  joinProjectRoom(projectId: string, userId: string, token?: string): void {
    this.connectionState.rooms.add(projectId);
    this.emit("room:join", { projectId, userId, token });
    logger.info("[SocketService] Joined project room", { projectId, userId });
  }

  /**
   * Leaves a project room.
   */
  leaveProjectRoom(projectId: string, userId: string): void {
    this.connectionState.rooms.delete(projectId);
    this.emit("room:leave", { projectId, userId });
    logger.info("[SocketService] Left project room", { projectId, userId });
  }

  /**
   * Re-joins all rooms after reconnect.
   * Called automatically on reconnect.
   */
  private rejoinRooms(): void {
    for (const projectId of this.connectionState.rooms) {
      logger.info("[SocketService] Re-joining room after reconnect", { projectId });
      this.socket?.emit("room:join", { projectId });
    }
  }

  /* =========================================================
     🔥 PRESENCE HELPERS
     ========================================================= */

  /**
   * Broadcasts cursor position to all collaborators in the project.
   */
  broadcastCursor(data: Omit<PresenceCursorEvent, "timestamp">): void {
    this.emit("presence:cursor", data);
  }

  /**
   * Broadcasts active file change to all collaborators.
   */
  broadcastActiveFile(data: Omit<PresenceFileEvent, "timestamp">): void {
    this.emit("presence:file", data);
  }

  /**
   * Broadcasts presence status (active/idle/away).
   */
  broadcastStatus(data: Omit<PresenceStatusEvent, "timestamp">): void {
    this.emit("presence:status", data);
  }

  /* =========================================================
     🔥 BUILD EVENT HELPERS
     ========================================================= */

  /**
   * Subscribes to all build events for a project.
   * Returns a cleanup function that removes all listeners.
   */
  subscribeToBuild(
    projectId: string,
    handlers: {
      onLog?:      (e: BuildLogEvent)      => void;
      onStatus?:   (e: BuildStatusEvent)   => void;
      onComplete?: (e: BuildCompleteEvent) => void;
      onError?:    (e: BuildErrorEvent)    => void;
    }
  ): () => void {
    this.emit("build:subscribe", { projectId });

    const unsubs: (() => void)[] = [];

    if (handlers.onLog) {
      unsubs.push(this.on("build:log", e => {
        if (e.projectId === projectId) handlers.onLog!(e);
      }));
    }
    if (handlers.onStatus) {
      unsubs.push(this.on("build:status", e => {
        if (e.projectId === projectId) handlers.onStatus!(e);
      }));
    }
    if (handlers.onComplete) {
      unsubs.push(this.on("build:complete", e => {
        if (e.projectId === projectId) handlers.onComplete!(e);
      }));
    }
    if (handlers.onError) {
      unsubs.push(this.on("build:error", e => {
        if (e.projectId === projectId) handlers.onError!(e);
      }));
    }

    return () => {
      this.emit("build:unsubscribe", { projectId });
      unsubs.forEach(fn => fn());
    };
  }

  /* =========================================================
     🔥 DEPLOYMENT EVENT HELPERS
     ========================================================= */

  /**
   * Subscribes to all deployment events for a project.
   * onComplete receives publicUrl (nuvrahq.com/p/:projectId)
   * wired from autoDeploymentManager.
   * Returns a cleanup function.
   */
  subscribeToDeployment(
    projectId:    string,
    deploymentId: string | undefined,
    handlers: {
      onProgress?: (e: DeploymentProgressEvent) => void;
      onComplete?: (e: DeploymentCompleteEvent) => void;
      onFailed?:   (e: DeploymentFailedEvent)   => void;
    }
  ): () => void {
    this.emit("deployment:subscribe", { projectId, deploymentId });

    const unsubs: (() => void)[] = [];

    if (handlers.onProgress) {
      unsubs.push(this.on("deployment:progress", e => {
        if (e.projectId === projectId) handlers.onProgress!(e);
      }));
    }
    if (handlers.onComplete) {
      unsubs.push(this.on("deployment:complete", e => {
        if (e.projectId === projectId) handlers.onComplete!(e);
      }));
    }
    if (handlers.onFailed) {
      unsubs.push(this.on("deployment:failed", e => {
        if (e.projectId === projectId) handlers.onFailed!(e);
      }));
    }

    return () => {
      this.emit("deployment:unsubscribe", { projectId });
      unsubs.forEach(fn => fn());
    };
  }

  /* =========================================================
     🔥 PRESENCE EVENT HELPERS
     ========================================================= */

  /**
   * Subscribes to all presence events for a project.
   * Returns a cleanup function.
   */
  subscribeToPresence(
    projectId: string,
    handlers: {
      onJoin?:    (e: PresenceJoinEvent)   => void;
      onLeave?:   (e: PresenceLeaveEvent)  => void;
      onCursor?:  (e: PresenceCursorEvent) => void;
      onFile?:    (e: PresenceFileEvent)   => void;
      onStatus?:  (e: PresenceStatusEvent) => void;
      onList?:    (e: PresenceListEvent)   => void;
      onAgentJoin?:  (e: AgentJoinEvent)   => void;
      onAgentState?: (e: AgentStateEvent)  => void;
      onAgentLeave?: (e: AgentLeaveEvent)  => void;
    }
  ): () => void {
    const unsubs: (() => void)[] = [];

    const guard = <T extends { projectId: string }>(
      fn: ((e: T) => void) | undefined,
      e:  T
    ) => { if (fn && e.projectId === projectId) fn(e); };

    if (handlers.onJoin)       unsubs.push(this.on("presence:join",   e => guard(handlers.onJoin,   e)));
    if (handlers.onLeave)      unsubs.push(this.on("presence:leave",  e => guard(handlers.onLeave,  e)));
    if (handlers.onCursor)     unsubs.push(this.on("presence:cursor", e => guard(handlers.onCursor, e)));
    if (handlers.onFile)       unsubs.push(this.on("presence:file",   e => guard(handlers.onFile,   e)));
    if (handlers.onStatus)     unsubs.push(this.on("presence:status", e => guard(handlers.onStatus, e)));
    if (handlers.onList)       unsubs.push(this.on("presence:list",   e => guard(handlers.onList,   e)));
    if (handlers.onAgentJoin)  unsubs.push(this.on("agent:join",      e => guard(handlers.onAgentJoin,  e)));
    if (handlers.onAgentState) unsubs.push(this.on("agent:state",     e => guard(handlers.onAgentState, e)));
    if (handlers.onAgentLeave) unsubs.push(this.on("agent:leave",     e => guard(handlers.onAgentLeave, e)));

    return () => unsubs.forEach(fn => fn());
  }

  /* =========================================================
     🔥 SESSION EVENT HELPERS
     ========================================================= */

  /**
   * Subscribes to session and lock events for a project.
   * Returns a cleanup function.
   */
  subscribeToSession(
    projectId: string,
    handlers: {
      onSessionCreated?:   (e: SessionCreatedEvent)    => void;
      onSessionClosed?:    (e: SessionClosedEvent)     => void;
      onLockAcquired?:     (e: FileLockEvent)          => void;
      onLockReleased?:     (e: FileLockReleasedEvent)  => void;
      onLockContested?:    (e: FileLockEvent)          => void;
      onOperation?:        (e: OperationEvent)         => void;
    }
  ): () => void {
    const unsubs: (() => void)[] = [];

    const guard = <T extends { projectId: string }>(
      fn: ((e: T) => void) | undefined,
      e:  T
    ) => { if (fn && e.projectId === projectId) fn(e); };

    if (handlers.onSessionCreated) unsubs.push(this.on("session:created",    e => guard(handlers.onSessionCreated,  e)));
    if (handlers.onSessionClosed)  unsubs.push(this.on("session:closed",     e => guard(handlers.onSessionClosed,   e)));
    if (handlers.onLockAcquired)   unsubs.push(this.on("lock:acquired",      e => guard(handlers.onLockAcquired,    e)));
    if (handlers.onLockReleased)   unsubs.push(this.on("lock:released",      e => guard(handlers.onLockReleased,    e)));
    if (handlers.onLockContested)  unsubs.push(this.on("lock:contested",     e => guard(handlers.onLockContested,   e)));
    if (handlers.onOperation)      unsubs.push(this.on("operation:recorded", e => guard(handlers.onOperation,       e)));

    return () => unsubs.forEach(fn => fn());
  }

  /* =========================================================
     🔥 SCHEMA SYNC HELPERS
     ========================================================= */

  /**
   * Subscribes to schema sync events for a project.
   * Returns a cleanup function.
   */
  subscribeToSchemaSync(
    projectId: string,
    handlers: {
      onUpdate?:   (e: SchemaSyncEvent)    => void;
      onConflict?: (e: SchemaConflictEvent) => void;
    }
  ): () => void {
    const unsubs: (() => void)[] = [];

    if (handlers.onUpdate) {
      unsubs.push(this.on("schema:update", e => {
        if (e.projectId === projectId) handlers.onUpdate!(e);
      }));
    }
    if (handlers.onConflict) {
      unsubs.push(this.on("schema:conflict", e => {
        if (e.projectId === projectId) handlers.onConflict!(e);
      }));
    }

    return () => unsubs.forEach(fn => fn());
  }

  /**
   * Broadcasts a schema change to all collaborators in the project.
   */
  broadcastSchemaChange(data: Omit<SchemaSyncEvent, "timestamp">): void {
    this.emit("schema:broadcast", data);
  }

  /* =========================================================
     🔥 CONNECTION STATE
     ========================================================= */

  /**
   * Returns the current connection state.
   */
  getConnectionState(): Readonly<SocketConnectionState> {
    return { ...this.connectionState, rooms: new Set(this.connectionState.rooms) };
  }

  /**
   * Returns true if the socket is currently connected.
   */
  isConnected(): boolean {
    return this.socket?.connected === true;
  }

  /**
   * Subscribe to connection state changes.
   * Returns an unsubscribe function.
   */
  onConnectionStateChange(
    listener: (state: SocketConnectionState) => void
  ): () => void {
    this.connectionListeners.add(listener);
    listener(this.getConnectionState());
    return () => this.connectionListeners.delete(listener);
  }

  /* =========================================================
     🔥 AUTH TOKEN
     ========================================================= */

  /**
   * Updates the auth token and reconnects if already connected.
   * Call this after login / token refresh.
   */
  setAuthToken(token: string): void {
    this.authToken = token;
    if (this.socket) {
      // Reconnect with new token
      (this.socket.auth as Record<string, unknown>)["token"] = token;
      this.socket.disconnect().connect();
      logger.info("[SocketService] Auth token updated - reconnecting");
    }
  }

  /* =========================================================
     🔥 HEARTBEAT
     ========================================================= */

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.pingTimestamp = Date.now();
        this.socket.emit("heartbeat:ping", { timestamp: this.pingTimestamp });
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /* =========================================================
     🔥 EVENT QUEUE
     ========================================================= */

  /**
   * Queues an event to be sent when the connection is restored.
   */
  private queueEvent(event: keyof SocketEmitMap, data: unknown): void {
    if (this.eventQueue.length >= MAX_EVENT_QUEUE_SIZE) {
      // Drop oldest event to make room
      this.eventQueue.shift();
      logger.warn("[SocketService] Event queue full - dropped oldest event");
    }

    this.eventQueue.push({ event, data, queuedAt: Date.now() });
  }

  /**
   * Flushes queued events to the server after reconnect.
   * Drops events older than 60 seconds - they're stale.
   */
  private flushEventQueue(): void {
    if (this.eventQueue.length === 0) return;

    const staleThreshold = Date.now() - 60_000;
    const fresh          = this.eventQueue.filter(e => e.queuedAt > staleThreshold);
    const dropped        = this.eventQueue.length - fresh.length;

    if (dropped > 0) {
      logger.warn("[SocketService] Dropped stale queued events", { dropped });
    }

    this.eventQueue = [];

    for (const { event, data } of fresh) {
      logger.info("[SocketService] Flushing queued event", { event });
      this.socket?.emit(event, data);
    }
  }

  /* =========================================================
     HELPERS
     ========================================================= */

  private updateConnectionState(patch: Partial<SocketConnectionState>): void {
    this.connectionState = { ...this.connectionState, ...patch };
    const snapshot       = this.getConnectionState();
    this.connectionListeners.forEach(fn => { try { fn(snapshot); } catch { /* safe */ } });
  }

  /* =========================================================
     🔥 LEGACY HELPERS (backward compatible)
     ─────────────────────────────────────────────────────────
     Kept so existing callers don't break while migrating to
     the typed on() / emit() API above.
     ========================================================= */

  /** @deprecated Use on("build:log", cb) instead */
  onBuildLog(callback: (log: string) => void): void {
    this.on("build:log", e => callback(e.message));
  }

  /** @deprecated Use on("build:status", cb) instead */
  onBuildStatus(callback: (status: BuildStatusEvent) => void): void {
    this.on("build:status", callback);
  }

  /** @deprecated Use on("build:complete", cb) instead */
  onBuildComplete(callback: (result: BuildCompleteEvent) => void): void {
    this.on("build:complete", callback);
  }

  /** @deprecated Use on("build:error", cb) instead */
  onBuildError(callback: (error: string) => void): void {
    this.on("build:error", e => callback(e.message));
  }

  /** @deprecated Use emit("room:join", ...) instead */
  emitRaw(event: string, data?: unknown): void {
    if (!this.socket) {
      logger.warn("[SocketService] Socket not initialized");
      return;
    }
    this.socket.emit(event, data);
  }

  /* =========================================================
     🔥 DISCONNECT / DESTROY
     ========================================================= */

  /**
   * Gracefully disconnects and cleans up all resources.
   */
  disconnect(): void {
    this.stopHeartbeat();

    if (this.socket) {
      logger.info("[SocketService] Disconnecting");
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.listeners.clear();
    this.connectionListeners.clear();
    this.eventQueue = [];
    this.connectionState.rooms.clear();

    this.updateConnectionState({
      status:   "disconnected",
      socketId: null,
    });
  }

  /**
   * Removes all listeners for a specific event.
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.listeners.delete(event);
      this.socket?.removeAllListeners(event);
    } else {
      this.listeners.clear();
      this.socket?.removeAllListeners();
    }
  }

  /**
   * Returns the raw Socket.IO instance.
   * Use sparingly - prefer the typed API above.
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

/* =========================================================
   🔥 SINGLETON EXPORT
   ========================================================= */

export const socketService = new SocketService();

/**
 * Legacy function exports - backward compatible.
 * Migrate callers to socketService.on() / socketService.emit().
 */
export function initializeSocket(
  onConnect?:    () => void,
  onDisconnect?: () => void
): Socket {
  return socketService.initialize({ onConnect, onDisconnect });
}

export function getSocket(): Socket | null {
  return socketService.getSocket();
}

export function disconnectSocket(): void {
  socketService.disconnect();
}

export function emit(event: string, data?: unknown): void {
  socketService.emitRaw(event, data);
}

export function removeAllListeners(event?: string): void {
  socketService.removeAllListeners(event);
}

export function onBuildLog(callback: (log: string) => void): void {
  socketService.onBuildLog(callback);
}

export function onBuildStatus(callback: (status: BuildStatusEvent) => void): void {
  socketService.onBuildStatus(callback);
}

export function onBuildComplete(callback: (result: BuildCompleteEvent) => void): void {
  socketService.onBuildComplete(callback);
}

export function onBuildError(callback: (error: string) => void): void {
  socketService.onBuildError(callback);
}

export default socketService;
